import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { toast } from 'App';

const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours
const WARNING_TIMEOUT = 1 * 60 * 60 * 1000 + 59 * 60 * 1000 + 30 * 1000; // 1 hour 59 min 30 sec

export const useInactivityTimer = () => {
  const { logout } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isLoggedOutRef = useRef<boolean>(false);

  const resetTimer = useCallback(() => {
    if (isLoggedOutRef.current) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    lastActivityRef.current = Date.now();

    warningTimeoutRef.current = setTimeout(() => {
      if (!isLoggedOutRef.current) {
        toast({
          title: 'Session Expiring Soon',
          description: 'You will be logged out in 5 seconds due to inactivity.',
          status: 'warning',
          duration: 10000,
          isClosable: true,
        });
      }
    }, WARNING_TIMEOUT);

    timeoutRef.current = setTimeout(async () => {
      if (!isLoggedOutRef.current) {
        isLoggedOutRef.current = true;

        toast({
          title: 'Session Expired',
          description: 'You have been logged out due to inactivity.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });

        await logout();
      }
    }, INACTIVITY_TIMEOUT);
  }, [logout]);

  const handleUserActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus',
    ];

    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    resetTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [handleUserActivity, resetTimer]);

  const resetInactivityTimer = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  return { resetInactivityTimer };
};
